
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

interface EditDiscountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  discount: {
    id: number;
    title: string;
    code: string;
    description: string | null;
    status: string;
    discount_type: string;
    discount_percentage: number | null;
    discount_amount: number | null;
    combinations: boolean;
    valid_until: string | null;
  };
}

export function EditDiscountDialog({ open, onOpenChange, discount }: EditDiscountDialogProps) {
  const [title, setTitle] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [discountValue, setDiscountValue] = useState('');
  const [discountType, setDiscountType] = useState('percentage');
  const [isActive, setIsActive] = useState(true);
  const [allowCombinations, setAllowCombinations] = useState(true);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (discount) {
      setTitle(discount.title);
      setCode(discount.code);
      setDescription(discount.description || '');
      setDiscountType(discount.discount_type);
      setDiscountValue(
        discount.discount_type === 'percentage' 
          ? String(discount.discount_percentage || '') 
          : String(discount.discount_amount || '')
      );
      setIsActive(discount.status === 'active');
      setAllowCombinations(discount.combinations);
    }
  }, [discount]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate discount value is a number
    const parsedValue = parseFloat(discountValue);
    if (isNaN(parsedValue)) {
      toast({
        variant: "destructive",
        title: "Invalid value",
        description: "Please enter a valid number for the discount value",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('discount_codes')
        .update({
          title,
          code,
          description,
          discount_percentage: discountType === 'percentage' ? parsedValue : null,
          discount_amount: discountType === 'amount' ? parsedValue : null,
          discount_type: discountType,
          status: isActive ? 'active' : 'inactive',
          combinations: allowCombinations,
        })
        .eq('id', discount.id);

      if (error) {
        console.error("Supabase error:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: `Failed to update discount code: ${error.message}`,
        });
        return;
      }

      toast({
        title: "Success",
        description: "Discount code updated successfully",
      });

      queryClient.invalidateQueries({ queryKey: ['discounts'] });
      onOpenChange(false);
    } catch (err) {
      console.error("Unexpected error:", err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Discount</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="code">Code</Label>
            <Input
              id="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Discount Type</Label>
            <RadioGroup 
              value={discountType} 
              onValueChange={setDiscountType}
              className="flex space-x-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="percentage" id="edit-percentage" />
                <Label htmlFor="edit-percentage">Percentage (%)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="amount" id="edit-amount" />
                <Label htmlFor="edit-amount">Fixed Amount ($)</Label>
              </div>
            </RadioGroup>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="discountValue">
              {discountType === 'percentage' ? 'Discount Percentage (%)' : 'Discount Amount ($)'}
            </Label>
            <Input
              id="discountValue"
              type="number"
              min="0"
              max={discountType === 'percentage' ? "100" : undefined}
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              required
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="active"
              checked={isActive}
              onCheckedChange={setIsActive}
            />
            <Label htmlFor="active">Active</Label>
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="combinations"
              checked={allowCombinations}
              onCheckedChange={setAllowCombinations}
            />
            <Label htmlFor="combinations">Allow combinations with other discounts</Label>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">Save Changes</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
